import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

const defaultLocale = navigator.language;

class DateUtils {
    getDateString = (date) => {
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const day = ('0' + date.getDate()).slice(-2);

        return `${date.getFullYear()}-${month}-${day}`;
    }

    getTimeString = (date) => {
        return `${('0' + date.getHours()).slice(-2)}:${('0' + date.getMinutes()).slice(-2)}`;
    }

    getExtendedDate = (date, locale = defaultLocale) => {
        if (!(date instanceof Date)) {
            date = new Date(date);
        }

        const monthName = date.toLocaleString(locale, { month: 'long' });
        const monthNameShort = date.toLocaleString(locale, { month: 'short' });
        const dateString = this.getDateString(date);
        const timeString = this.getTimeString(date);
        const dayOfWeek = date.toLocaleString(locale, { weekday: 'short' });

        const extendedDate = {
            timestamp: date.getTime(),
            dayOfMonth: date.getDate(),
            dayOfWeek,
            dateString,
            monthName,
            monthNameShort,
            year: date.getFullYear(),
            time: timeString,
        }

        return extendedDate;
    }

    getLengthOfStay = (checkin, checkout) => {
        return Math.ceil((new Date(checkout) - new Date(checkin)) / (60 * 60 * 24 * 1000));
    }

    parseDatesToTimestamps = (object) => {
        for (const property in object) {
            if (object[property] instanceof Date) {
                const date = object[property];
                object[property] = `${this.getDateString(date)}T${this.getTimeString(date)}`
            }
        }
    }

    parseTimestampsToDates = (object) => {
        dayjs.extend(utc);
        dayjs.extend(timezone);

        for (const property in object) {
            const value = object[property];
            const dateWithTimePattern = /(\d\d\d\d-\d\d-\d\dT[\d:]*)/g;
            const datePattern = /(\d\d\d\d-\d\d-\d\d)/g;

            if (typeof (value) === 'string' && dateWithTimePattern.test(value)) {
                object[property] = dayjs.tz(value, 'Europe/Berlin').toDate();//keep the local timezone, not UTC
            }
            else if (typeof (value) === 'string' && datePattern.test(value)) {
                object[property] = dayjs.tz(`${value}T00:00:00`, 'Europe/Berlin').toDate();//keep the local timezone, not UTC
            }
        }
    }

    atMidnight = (date) => {
        const newDate = new Date(date.getTime());
        newDate.setHours(0);
        newDate.setMinutes(0);
        newDate.setSeconds(0);

        return newDate;
    }

    getLocalizedDayName = (number, locale) => {
        const baseDate = new Date(2023, 0, 1); // Arbitrary base date (e.g., Jan 1, 2023)
        const baseDayOfWeek = baseDate.getDay(); // Get the day of the week for Jan 1, 2023 (0 = Sunday)

        const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' }); // Use 'long' for full day names

        const dayOffset = (number) - baseDayOfWeek; // Adjust for the base day's offset
        const targetDate = new Date(baseDate);
        targetDate.setDate(baseDate.getDate() + dayOffset); // Adjust date dynamically
        return formatter.format(targetDate);
    };

    getFormattedDate = date => date?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    getDaysOfWeekListFromFlag = (flag) => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            if (flag & (1 << i)) {
                days.push(i + 1);
            }
        }
        return days;
    }

    getFlagFromDaysOfWeekList = (days) => {
        return days.reduce((flag, day) => flag | (1 << (day - 1)), 0);
    };
}

export const dateUtils = new DateUtils();

// --- Calendar availability month helpers (picker prefetch windows)

/**
 * @typedef {{ year: number, month: number }} YearMonth Calendar month with month 1–12.
 */

/** @param {YearMonth} s */
export const yearMonthKey = (s) => `${s.year}-${String(s.month).padStart(2, '0')}`;

/** @param {YearMonth} spec */
export const addCalendarMonths = (spec, delta) => {
    const d = new Date(spec.year, spec.month - 1 + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

/**
 * For each calendar month in {@code specs}, includes that month and the previous and next month.
 * @param {YearMonth[]} specs
 * @returns {YearMonth[]}
 */
export const extendMonthsWithNeighbors = (specs) => {
    const out = new Map();
    for (const m of specs) {
        for (const delta of [-1, 0, 1]) {
            const o = addCalendarMonths(m, delta);
            out.set(yearMonthKey(o), o);
        }
    }
    return Array.from(out.values());
};

/** @param {Date[]} monthStartDates */
export const monthStartsToYearMonths = (monthStartDates) =>
    monthStartDates.map((d) => ({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
    }));